import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import date from 'date-and-time';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { daysToRelease } from 'fym-shared/src/music24/releases';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { useCreateKitAssetsMutation } from '../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useTRPCAPI, useTRPCClientAPI } from '../../../../trpc/trpc';
import { SMLAnalytics } from '../../../analytics/sml/analytics';
import { firstKitItems, newSongContext } from '../../../onboarding/addSong/firstKitItems';
import { useMusic24Capabilities } from '../../../music24/hooks/useMusic24Capabilities';
import { presaveSongState, todayUtc } from '../../presaveCreate/presaveCreateFacts';
import { CreatedPresave, useCreatePresaveFromSong } from '../../presaveCreate/useCreatePresaveFromSong';

/** How the first song came in (the analytics of design onboarding c4). */
export type FirstSongKind = 'claim' | 'link' | 'unreleased';

/**
 * True when Finish makes the pre-save page of the first song (QA F03, decision D10): the plan has
 * pre-saves and the song is unreleased, has a release day of today or later and no pre-save page.
 */
export function shouldMakeFirstPresave(item: CatalogItem, canUsePresave: boolean, now: number): boolean {
  return canUsePresave && presaveSongState(item, todayUtc(now)) === 'ready';
}

/**
 * The onboarding part of "Add my song", which runs the same steps as the Catalog page
 * (design catalog-add-song f4, onboarding c4):
 * - `markFirstRelease`: right after the song is saved, `onboarding.setFirstRelease`, so a user who
 *   closes the page half way still has the song as the first release;
 * - `startFirstImages`: at Done, the free images of the default kit (`batch: true`). A 402 or a 409
 *   is a kit state, not a failure: the release page shows it;
 * - `startPresavePage`: at Done, the published pre-save page of a song with a future release day,
 *   on a plan with pre-saves. The same two calls as "New SmartLink > Pre-save page"
 *   (`useCreatePresaveFromSong`). It answers the made page, or null when it made none.
 */
export function useOnboardingFirstSong(accountId: number) {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();
  const [createKitAssets] = useCreateKitAssetsMutation();
  const { canUsePresave } = useMusic24Capabilities();
  const { create: createPresave } = useCreatePresaveFromSong(accountId);

  const markFirstRelease = useCallback(
    async (catalogItemUuid: string, kind: FirstSongKind): Promise<void> => {
      try {
        await trpcClient.onboarding.setFirstRelease.mutate({ catalogItemUuid });
      } catch (error) {
        // The song is in the catalog. The checklist picks the newest row when this call fails.
        console.error(error);
      }
      SMLAnalytics.track('onboarding_first_song_added', { kind }).catch(console.error);
      queryClient.invalidateQueries({ queryKey: trpcAPI.onboarding.state.queryKey() }).catch(console.error);
    },
    [queryClient, trpcAPI, trpcClient]
  );

  const startFirstImages = useCallback(
    async (item: CatalogItem): Promise<void> => {
      const today = date.format(new Date(), 'YYYY-MM-DD');
      const releaseDate = item.releaseDate === null ? null : item.releaseDate.slice(0, 10);
      const items = firstKitItems({ releaseDate, today, song: newSongContext(daysToRelease(releaseDate, today)) });
      if (items.length === 0) {
        return;
      }
      try {
        await createKitAssets({ accountId, catalogItemUuid: item.uuid, items, batch: true }).unwrap();
      } catch (kitError) {
        if (!(kitError instanceof FYMAPIError && (kitError.statusCode === 402 || kitError.statusCode === 409))) {
          console.error('Failed to start the first images', kitError);
        }
      }
    },
    [accountId, createKitAssets]
  );

  const startPresavePage = useCallback(
    async (item: CatalogItem): Promise<CreatedPresave | null> => {
      if (!shouldMakeFirstPresave(item, canUsePresave, Date.now())) {
        return null;
      }
      // A draft (publish refused), a plan limit or a failure leaves the release page as it is: the
      // Overview offers the pre-save page there. When the server already made the page of this song
      // (`onboarding.setFirstRelease` makes it too), the wizard answers that page (200,
      // `existing: true`) and makes no second one: it is a success like a new page.
      const outcome = await createPresave(item, defaultPresaveServices);
      return outcome.kind === 'created' ? outcome.page : null;
    },
    [canUsePresave, createPresave]
  );

  return useMemo(
    () => ({ markFirstRelease, startFirstImages, startPresavePage }),
    [markFirstRelease, startFirstImages, startPresavePage]
  );
}
